import { gql, useApolloClient } from "@apollo/client";
import { useCallback, useEffect, useState } from "react";

import courseQueryResult from "../../../tests/__results__/courseQueryResult";
import { CourseFragment } from "../../hooks/__generated__/CourseFragment";
import { TrainingInstanceFragment } from "../../hooks/__generated__/TrainingInstanceFragment";
import { useAppState } from "../../hooks/useAppState";
import { SLIDE_VIEWS_QUERY, slideViewsLoadingVar, useCourseProgress } from "../../hooks/useCourseProgress";
import { useTestMode } from "../../hooks/useTestMode";
import { getFirstSectionIndex } from "../../util/course";
import { CourseQuery_course, CourseQuery_instance } from "./__generated__/CourseQuery";

export const COURSE_QUERY = gql`
  query CourseQuery($courseId: Int!) {
    course(courseId: $courseId) {
      id
      courseType
      shortName
      name
      trainingId
      availableResolutions
      fullVersion
      version
      complete
      messages {
        text
        link
      }
      sections {
        id
        name
        number
        surveyUrl
        modules {
          id
          name
          type
          sectionId
          fullNumber
          duration
          slides {
            id
            moduleId
            name
            number
            fullNumber
            duration
          }
        }
      }
    }
    instance(courseId: $courseId) {
      id
      expirationDateTime
      userId
      progress {
        instanceId
        viewedSeconds
        totalSeconds
        viewedSlides
        totalSlides
        totalQuizzes
        passedQuizzes
        complete
        lastView {
          slideId
          resumeTime
          dateTimeViewed
        }
        moduleProgress {
          id
          name
          type
          fullNumber
          viewedSlides
          totalSlides
          quizScore
        }
      }
    }
  }
`;

export const FULL_COURSE_QUERY = gql`
  query FullCourseQuery($courseId: Int!) {
    course(courseId: $courseId) {
      trainingId
      contributors {
        id
        bio {
          firstName
          lastName
        }
        profile {
          name
          image
          text
          link
        }
      }
      resources {
        title
        resourceHost
        linkJson
      }
      messages {
        text
        link
      }
      sections {
        id
        modules {
          id
          quizScore
          confidenceRating {
            moduleId
            rating
          }
          slides {
            id
            notes {
              slideId
              content
              updated
            }
            bookmarks {
              slideId
              timestamp
            }
          }
        }
      }
    }
  }
`;

export const hasSlideInCourse = (data: CourseQuery_course, slideIdToFind: string): boolean => {
  for (const section of data.sections) {
    for (const module of section.modules) {
      for (const slide of module.slides) {
        if (slide.id === slideIdToFind) {
          return true;
        }
      }
    }
  }
  return false;
};

export function useCourseQuery(): CourseFragment | undefined {
  const { testMode } = useTestMode();
  const {
    appState: { courseId, moduleId, slideId, instanceId, course, instance },
    setSlide,
    setInstance,
    setModule,
    setCourse,
  } = useAppState();
  const client = useApolloClient();
  const { updateCourseProgressState, syncCourseSlideViews } = useCourseProgress(
    courseId ?? undefined,
    course?.id,
    instanceId ?? undefined,
  );

  const [queryInstance, setQueryInstance] = useState<TrainingInstanceFragment>();
  const [queryCourse, setQueryCourse] = useState<CourseFragment>();
  const [courseData, setCourseData] = useState<CourseQuery_course>();
  const [loading, setLoading] = useState(false);
  const [queryError, setQueryError] = useState(false);

  const queryFullCourse = useCallback(async () => {
    try {
      setLoading(true);
      const result = await client.query({
        query: FULL_COURSE_QUERY,
        variables: { courseId },
        errorPolicy: "all",
      });

      // Check for null course data before setting
      if (result.data?.course && courseId) {
        setCourse(courseId);
        updateCourseProgressState();
        setQueryError(false);
      } else {
        console.error("Full course data is null/undefined", result.data);
        setQueryError(true);
      }
      setLoading(false);
    } catch (e) {
      console.error("ERROR", e);
      setQueryError(true);
      setLoading(false);
    }
  }, [courseId, updateCourseProgressState, client, setCourse]);

  const queryPartCourse = useCallback(async () => {
    try {
      setLoading(true);
      const result = await client.query({
        query: COURSE_QUERY,
        variables: { courseId },
      });

      // Check for null course/instance data before destructuring
      if (result.data?.course && result.data?.instance) {
        const { course, instance } = result.data;
        setCourseData(course);
        setCourse(course.trainingId);
        setInstance(instance.id);
        setQueryError(false);
      } else {
        console.error("Course or instance data is null/undefined", result.data);
        setQueryError(true);
      }
      setLoading(false);
    } catch (e) {
      console.error("ERROR", e);
      setQueryError(true);
      setLoading(false);
    }
  }, [courseId, setCourse, setInstance, client]);

  useEffect(() => {
    if (testMode) {
      setCourse();
    } else if (courseId && !testMode && !loading && !queryError) {
      if (!course) {
        queryPartCourse();
      } else if (!course.contributors) {
        queryFullCourse();
      }
    }
  }, [courseId, testMode, queryFullCourse, queryPartCourse, course, loading, queryError, setCourse]);

  const initializeTestMode = useCallback(() => {
    if (!slideId) {
      setQueryCourse(course);
      const firstSectionIndex = getFirstSectionIndex(courseQueryResult.data.course.sections);
      const firstModuleId = courseQueryResult.data.course.sections[firstSectionIndex].modules[0].id;
      setModule(firstModuleId);
    }

    setQueryInstance(courseQueryResult.data.instance as unknown as CourseQuery_instance);
  }, [course, slideId, setModule]);

  const syncCourseData = useCallback(() => {
    if (course && course.id !== queryCourse?.id) {
      setQueryCourse(course);
    }
  }, [course, queryCourse?.id]);

  const initializeFromInstance = useCallback(() => {
    if (!course || moduleId || slideId || !instance) return;

    if (instance.progress?.lastView) {
      const lastViewSlideId = instance.progress.lastView.slideId;
      const courseHasLastViewSlide = courseData ? hasSlideInCourse(courseData, lastViewSlideId) : false;

      if (courseData && !courseHasLastViewSlide) {
        console.warn("Last viewed slide not found in course data, falling back to first module", {
          slideId: lastViewSlideId,
          courseId,
        });
        const firstSectionIndex = getFirstSectionIndex(courseData.sections);
        const firstModuleId = courseData.sections[firstSectionIndex]?.modules[0]?.id;
        if (firstModuleId) {
          setModule(firstModuleId);
        }
        return;
      }

      setSlide(lastViewSlideId, instance.progress.lastView.resumeTime ?? undefined);
      return;
    }

    const firstSectionIndex = getFirstSectionIndex(course.sections);
    const firstModuleId = course.sections[firstSectionIndex].modules[0].id;
    setModule(firstModuleId);
  }, [course, courseData, courseId, instance, moduleId, setModule, setSlide, slideId]);

  const syncInstance = useCallback(() => {
    if (instance && instance !== queryInstance) {
      setQueryInstance(instance);
      setInstance(instance.id);
    }
  }, [instance, queryInstance, setInstance]);

  useEffect(() => {
    if (testMode) {
      initializeTestMode();
      return;
    }

    syncCourseData();
    initializeFromInstance();
    syncInstance();
  }, [
    courseData,
    moduleId,
    slideId,
    testMode,
    course,
    instance,
    initializeFromInstance,
    initializeTestMode,
    syncCourseData,
    syncInstance,
  ]);

  // Fetch all slide views in the background to populate cache for accurate progress calculation
  useEffect(() => {
    if (testMode || !courseId || !instanceId || !course) return;

    // Set loading state for progress indicator
    slideViewsLoadingVar(true);

    // Non-blocking fetch to populate slide lastView cache
    client
      .query({
        query: SLIDE_VIEWS_QUERY,
        variables: { courseId },
        fetchPolicy: "network-only", // Force fresh data from server
      })
      .then(() => {
        // Recalculate progress after cache is populated with all slide views
        syncCourseSlideViews();
        // Update the reactive variable so the UI reflects the new values
        updateCourseProgressState();
      })
      .catch(error => {
        // Log but don't block - this is a background optimization
        console.warn("Failed to fetch slide views for progress calculation:", error);
      })
      .finally(() => {
        slideViewsLoadingVar(false);
      });
  }, [client, courseId, instanceId, course, testMode, syncCourseSlideViews, updateCourseProgressState]);

  return course;
}
